/** UI etiketleri ve görsel yardımcılar (dile duyarlı). */

import type { Confidence, IssueCategory, Severity } from '../types';
import { i18n, t } from '../i18n/state.svelte';
import type { Lang } from '../i18n/types';

export const SEVERITY_UI: Record<
	Severity,
	{ chip: string; dot: string; icon: string }
> = {
	critical: { chip: 'chip-critical', dot: '#b3261e', icon: '!' },
	warning: { chip: 'chip-warning', dot: '#9a5b06', icon: '!' },
	info: { chip: 'chip-info', dot: '#1f5f8b', icon: 'i' },
	ok: { chip: 'chip-ok', dot: '#3f6212', icon: '✓' }
};

export function severityLabel(severity: Severity): string {
	return t(`severity.${severity}`);
}

export function severityHint(severity: Severity): string {
	return t(`severity.${severity}.hint`);
}

export const CONFIDENCE_UI: Record<Confidence, { tone: string }> = {
	high: { tone: '#0f766e' },
	medium: { tone: '#9a5b06' },
	low: { tone: '#78716c' }
};

export function confidenceLabel(c: Confidence): string {
	return t(`confidence.${c}`);
}

export function confidenceShort(c: Confidence): string {
	return t(`confidence.${c}.short`);
}

export function categoryLabel(c: IssueCategory): string {
	return t(`category.${c}`);
}

export function categoryShort(c: IssueCategory): string {
	return t(`category.short.${c}`);
}

export function designLabel(design: string): string {
	return t(`design.${design}`);
}

export function gradeTone(grade: string): string {
	switch (grade) {
		case 'A':
			return '#3f6212';
		case 'B':
			return '#0f766e';
		case 'C':
			return '#9a5b06';
		default:
			return '#b3261e';
	}
}

export function scoreTone(score: number): string {
	if (score >= 90) return '#3f6212';
	if (score >= 75) return '#0f766e';
	if (score >= 60) return '#9a5b06';
	return '#b3261e';
}

export function locale(): string {
	return i18n.lang === 'tr' ? 'tr-TR' : 'en-US';
}

export function formatNumber(n: number, digits = 3): string {
	if (!Number.isFinite(n)) return '—';
	return n.toFixed(digits).replace(/^0\./, '.');
}

export function formatPValue(p: number | undefined): string {
	if (p === undefined || !Number.isFinite(p)) return '—';
	if (p < 0.001) return '< .001';
	return p.toFixed(3).replace(/^0/, '.');
}

export function formatCount(n: number): string {
	return n.toLocaleString(locale());
}

export function relativeTime(iso: string): string {
	const then = new Date(iso).getTime();
	const diff = Date.now() - then;
	const min = Math.round(diff / 60000);
	if (min < 1) return t('time.justNow');
	if (min < 60) return t('time.minutesAgo', { n: min });
	const h = Math.round(min / 60);
	if (h < 24) return t('time.hoursAgo', { n: h });
	return new Date(iso).toLocaleDateString(locale());
}

export type { Lang };
